import { Card, CardContent } from '../ui';
import { positionMeta, STANDING_RULE } from '../../constants/standing';
import { riskBand, riskLabel, RISK_BANDS } from '../../constants/risk';
import { STANCE_META } from '../../constants/sentiment';
import { relativeTime } from '../../utils/dates';
import { cn } from '../../utils/cn';

/**
 * Exposure x Standing, read together.
 *
 * The product used to show "At risk · 77" on one screen and "Admiration" on
 * another with nothing between them, so a manager could not tell whether the
 * account was in trouble. These two numbers measure different things -
 * outsiders' reaction to what they post, and their own audience's reaction to
 * them - and a creator can be loved AND dangerous. This card puts the position
 * first, then each axis under a label that says what it actually measures, then
 * the saved narrative. Nothing here is computed on the client: `standing` is
 * written by the backend so every screen shows the same position.
 */
export function StandingCard({ account, compact = false, className }) {
	const s = account?.standing;
	const meta = positionMeta(s?.position);

	if (!s?.position) {
		return (
			<Card className={className}>
				<CardContent className="p-5 text-sm text-muted-foreground">
					We cannot place this account yet. Run a scan and take a pulse, and it appears here.
				</CardContent>
			</Card>
		);
	}

	const exposureScore = s.exposure?.score;
	const exposureBand = riskBand(exposureScore);
	const positioned = (s.for || 0) + (s.against || 0);
	const withPct = positioned ? Math.round(((s.for || 0) / positioned) * 100) : null;
	const againstPct = positioned ? Math.round(((s.against || 0) / positioned) * 100) : null;
	const total = positioned + (s.none || 0);

	return (
		<Card className={className}>
			<CardContent className={cn(compact ? 'p-4' : 'p-5')}>
				{/* Position first: the one word a manager is looking for. */}
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-sm font-semibold', meta.chip)}>
								<span className={cn('h-2 w-2 rounded-full', meta.dot)} />
								{meta.label}
							</span>
							{s.insight?.stale && <span className="text-xs text-muted-foreground">bringing this up to date…</span>}
						</div>
						<p className="mt-1.5 text-sm text-foreground">{meta.meaning}</p>
					</div>
					{s.computedAt && (
						<span className="shrink-0 text-xs text-muted-foreground">read {relativeTime(s.computedAt)}</span>
					)}
				</div>

				{/* Two axes, each labelled with what it measures. */}
				<div className={cn('mt-4 grid gap-4', compact ? 'sm:grid-cols-2' : 'md:grid-cols-2')}>
					<div className="rounded-lg border border-border bg-muted/60 p-3">
						<div className="flex items-baseline justify-between">
							<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Exposure</span>
							<span className="text-[11px] text-muted-foreground">what they post</span>
						</div>
						{exposureBand ? (
							<>
								<div className="mt-1.5 flex items-baseline gap-2">
									<span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', exposureBand.badge)}>{riskLabel(exposureScore)}</span>
								</div>
								<div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-accent" title={`${exposureScore} of 100`}>
									{RISK_BANDS.slice().reverse().map((b) => (
										<span key={b.key} className={cn('h-full', b.dot, 'opacity-25')} style={{ width: `${b.max - b.min + 1}%` }} />
									))}
								</div>
								<div className="relative -mt-1.5 h-1.5">
									<span className="absolute top-0 h-1.5 w-1 rounded-sm bg-foreground" style={{ left: `calc(${exposureScore}% - 2px)` }} />
								</div>
								<div className="mt-1.5 text-xs text-muted-foreground">
									{account.lastRiskUpdate ? `last scored ${relativeTime(account.lastRiskUpdate)}` : 'from the latest scans'}
								</div>
							</>
						) : (
							<p className="mt-1.5 text-sm text-muted-foreground">Not scored yet - run a scan.</p>
						)}
					</div>

					<div className="rounded-lg border border-border bg-muted/60 p-3">
						<div className="flex items-baseline justify-between">
							<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Standing</span>
							<span className="text-[11px] text-muted-foreground">what the people replying say</span>
						</div>
						{s.runId && positioned > 0 ? (
							<>
								<div className="mt-1.5 text-sm tabular-nums">
									<span className={cn('font-medium', STANCE_META.for.text)}>{withPct}% with</span>
									<span className="text-muted-foreground"> · </span>
									<span className={cn('font-medium', STANCE_META.against.text)}>{againstPct}% against</span>
									<span className="ml-1.5 text-xs text-muted-foreground">of {positioned} with a position</span>
								</div>
								<div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-accent" title={`${s.for} with · ${s.none} no position · ${s.against} against`}>
									{s.for > 0 && <span className={STANCE_META.for.bg} style={{ width: `${(s.for / total) * 100}%` }} />}
									{s.none > 0 && <span className={STANCE_META.none.bg} style={{ width: `${(s.none / total) * 100}%` }} />}
									{s.against > 0 && <span className={STANCE_META.against.bg} style={{ width: `${(s.against / total) * 100}%` }} />}
								</div>
								<div className="mt-1.5 text-xs text-muted-foreground">
									{s.runAt ? `last pulse ${relativeTime(s.runAt)}` : 'from the latest pulse'}
									{positioned < 30 && ' · too few to call yet'}
								</div>
							</>
						) : (
							<p className="mt-1.5 text-sm text-muted-foreground">No pulse yet - take one.</p>
						)}
					</div>
				</div>

				{/* The saved read. For an unknown position this is the reason itself. */}
				{s.insight?.summary && (
					<p className={cn('mt-4 text-sm leading-relaxed text-foreground', s.insight.stale && 'text-muted-foreground')}>
						{s.insight.summary}
					</p>
				)}
				{!s.insight?.summary && s.reason && (
					<p className="mt-4 text-sm text-muted-foreground">{s.reason}</p>
				)}

				{!compact && (
					<p className="mt-4 border-t border-border pt-3 text-[11px] leading-relaxed text-muted-foreground">{STANDING_RULE}</p>
				)}
			</CardContent>
		</Card>
	);
}
